import React, { useState } from 'react';
import { ReactIconPicker } from '@bizfirst/common-react';
import type { ProjectFormSubmitResult, ProjectFormValues, ProjectSummary } from './types';

const PROJECT_NAME_MAX_LENGTH = 40;

export interface ProjectFormProps {
  /** Present => edit mode; absent => create mode. Only Name/Description/... are read — a host's
   *  full Project entity satisfies this fine. */
  project?: ProjectSummary;
  /**
   * Host-owned submit. Flow Studio's host wires this to its existing multi-step
   * studioProjectApiClient.createWithStructure()/projectApiClient.update() orchestration
   * (including the auto-open-designer flow) so behavior is unchanged; App Studio's host wires
   * this to a two-call composition (projectApiClient.create() + AppsApiClient.create({
   * projectID })) — see that host's own onCreate implementation for why. This form owns only
   * field markup/validation-display, never API orchestration, so each host's create/update
   * semantics stay exactly as they are today.
   */
  onSubmit: (values: ProjectFormValues) => Promise<ProjectFormSubmitResult>;
  onCancel: () => void;
  createSubmitLabel?: string;
  editSubmitLabel?: string;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  onSubmit,
  onCancel,
  createSubmitLabel = 'Create Project',
  editSubmitLabel = 'Update Project',
}) => {
  const isEdit = !!project;

  const [formData, setFormData] = useState<ProjectFormValues>({
    Name: project?.Name || '',
    Description: project?.Description || '',
    LongDescription: project?.LongDescription || '',
    IconUrl: project?.IconUrl || '',
    Color: project?.Color || '#3b82f6',
    IsDefault: project?.IsDefault || false,
    IsShared: project?.IsShared || false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = <K extends keyof ProjectFormValues>(field: K, value: ProjectFormValues[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await onSubmit(formData);
      if (!result.success) {
        setError(result.errorMessage || (isEdit ? 'Failed to update project' : 'Failed to create project'));
      }
      // On success the host is responsible for closing the form / navigating — this component
      // has no opinion on what happens next (matches the old ProjectForm's onSuccess callback).
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while saving the project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {error && <div style={styles.formError}>{error}</div>}

        <div style={styles.formGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label style={styles.label}>
              Name <span style={{ color: '#f87171' }}>*</span>
            </label>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {formData.Name.length}/{PROJECT_NAME_MAX_LENGTH}
            </span>
          </div>
          <input
            type="text"
            style={styles.input}
            value={formData.Name}
            onChange={e => handleChange('Name', e.target.value.slice(0, PROJECT_NAME_MAX_LENGTH))}
            required
            placeholder="Enter project name"
            maxLength={PROJECT_NAME_MAX_LENGTH}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <input
            type="text"
            style={styles.input}
            value={formData.Description}
            onChange={e => handleChange('Description', e.target.value)}
            placeholder="Short description"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Long Description</label>
          <textarea
            style={{ ...styles.input, minHeight: 72, resize: 'vertical' }}
            value={formData.LongDescription}
            onChange={e => handleChange('LongDescription', e.target.value)}
            placeholder="Detailed description"
          />
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Icon</label>
            <ReactIconPicker value={formData.IconUrl} onChange={icon => handleChange('IconUrl', icon)} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Color</label>
            <input
              type="color"
              style={{ ...styles.input, padding: 2, height: 38, cursor: 'pointer' }}
              value={formData.Color}
              onChange={e => handleChange('Color', e.target.value)}
            />
          </div>
        </div>

        <div style={styles.formRow}>
          <label style={styles.checkboxRow}>
            <input type="checkbox" checked={formData.IsDefault} onChange={e => handleChange('IsDefault', e.target.checked)} />
            Set as Default
          </label>
          <label style={styles.checkboxRow}>
            <input type="checkbox" checked={formData.IsShared} onChange={e => handleChange('IsShared', e.target.checked)} />
            Shared Project
          </label>
        </div>
      </div>

      <div style={styles.formActions}>
        <button type="button" style={styles.secondaryButton} onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" style={styles.primaryButton} disabled={loading}>
          {loading ? 'Saving...' : isEdit ? editSubmitLabel : createSubmitLabel}
        </button>
      </div>
    </form>
  );
};

const styles: Record<string, React.CSSProperties> = {
  formGroup: { marginBottom: 16, flex: 1 },
  formRow: { display: 'flex', gap: 16 },
  label: {
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text-secondary, #cbd5e1)',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 12px',
    borderRadius: 6,
    border: '1px solid var(--color-border, #333)',
    background: 'var(--color-bg-secondary, #16161e)',
    color: 'var(--color-text-primary, #e8e8f0)',
    fontSize: 13,
    outline: 'none',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: 'var(--color-text-secondary, #cbd5e1)',
    cursor: 'pointer',
  },
  formError: {
    background: 'rgba(248,113,113,0.12)',
    border: '1px solid rgba(248,113,113,0.4)',
    color: '#fca5a5',
    borderRadius: 6,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
    whiteSpace: 'pre-wrap',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid var(--color-border, #2a2a3a)',
  },
  primaryButton: {
    padding: '9px 20px',
    borderRadius: 6,
    border: '1px solid #16a34a',
    background: '#16a34a',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '9px 20px',
    borderRadius: 6,
    border: '1px solid var(--color-border, #333)',
    background: 'transparent',
    color: 'var(--color-text-secondary, #cbd5e1)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
